import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Instagram, Linkedin } from 'lucide-react';

// TikTok icon (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// X (Twitter) icon
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const Footer = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  const socialLinks = [
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/seferyemek' },
    { name: 'TikTok', icon: TikTokIcon, url: 'https://tiktok.com/@seferyemek' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/seferyemek' },
    { name: 'X', icon: XIcon, url: 'https://x.com/seferyemek' },
  ];

  return (
    <footer ref={ref} className="bg-[#0A0A0A] border-t border-[#1A1A1A] py-12">
      <div className="max-w-8xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-center md:justify-start"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_sefer-istanbul/artifacts/90xvdv4m_sefer%20siyah%20back.png" 
              alt="SEFER"
              className="h-10"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col items-center justify-center gap-4"
          >
            <span 
              className="text-[#F0EBE3] text-base font-medium whitespace-nowrap"
              style={{ fontFamily: "'Ubuntu', sans-serif" }}
            >
              {data.tagline}
            </span>
            
            {/* Social Media Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                  whileHover={{ scale: 1.2, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-9 h-9 rounded-full bg-[#1A1A1A] flex items-center justify-center text-[#F0EBE3]/60 hover:text-[#C8102E] hover:bg-[#2A2A2A] transition-all duration-300"
                  title={social.name}
                >
                  <social.icon className="w-4 h-4" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.a
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
            href={`mailto:${data.email}`}
            className="flex items-center justify-center md:justify-end text-[#C8102E] hover:text-[#A00D25] transition-colors duration-300 text-sm"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            {data.email}
          </motion.a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center text-[#F0EBE3]/40 text-xs"
          style={{ fontFamily: "'Ubuntu', sans-serif" }}
        >
          {data.copyright}
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
